import { type ReactNode, useEffect } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCodeEditor,
  IgMonoText,
  IgTypography,
} from "@intelligems/ig-theme";
import { Divider, Stack } from "@mui/material";

import type { WorkspaceVersionDetail } from "../../types";

const fetchArtifactText = async (
  url: string | null,
  signal: AbortSignal
): Promise<string | null> => {
  if (!url) return null;
  const resp = await fetch(url, { signal });
  if (!resp.ok) return null;
  return resp.text();
};

/** The version-detail read, resolved by the domain's API port. */
export interface WorkspaceVersionDetailResult {
  detail: WorkspaceVersionDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  /**
   * Domain-specific summary rows (e.g. shipping's fallback policy) rendered
   * above the dependency list. Built by the binding so this view stays free
   * of any one domain's persisted controls.
   */
  extraRows?: ReactNode;
}

export interface VersionCodeDetailProps {
  version: number;
  /**
   * The resolved version-detail read. Passed as data rather than a hook so this
   * stays a plain presentational component: each domain wraps it with a thin
   * component that runs its own query.
   */
  versionDetail: WorkspaceVersionDetailResult;
  /** Splits the persisted source artifact into displayable files. */
  decodeFiles: (raw: string) => Record<string, string>;
  onImport: (version: number) => void;
  isImporting: boolean;
  /** `false` when the port supplies no versionImport capability. */
  canImport: boolean;
  readOnly: boolean;
}

/**
 * A published version's read-only code, rendered inline inside an expanded
 * version-history card. The card itself already carries the version number,
 * title, byline, and status chip, so this shows only the artifacts: source
 * files, dependencies, and the test suites — plus the import-as-draft action
 * for domains that support it.
 */
export const VersionCodeDetail = ({
  version,
  versionDetail,
  decodeFiles,
  onImport,
  isImporting,
  canImport,
  readOnly,
}: VersionCodeDetailProps) => {
  const { detail, isLoading, isError, extraRows } = versionDetail;

  const [artifacts, setArtifacts] = useImmer<{
    source: string | null;
    generatedTests: string | null;
    userTests: string | null;
  }>({ source: null, generatedTests: null, userTests: null });

  useEffect(() => {
    if (!detail) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const [source, generatedTests, userTests] = await Promise.all([
          fetchArtifactText(detail.sourceUrl, controller.signal),
          fetchArtifactText(detail.generatedTestsUrl, controller.signal),
          fetchArtifactText(detail.userTestsUrl, controller.signal),
        ]);
        setArtifacts({ source, generatedTests, userTests });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [detail, setArtifacts]);

  if (isLoading) {
    return (
      <IgTypography color="text.secondary">Loading version...</IgTypography>
    );
  }
  if (isError || !detail) {
    return (
      <IgTypography color="error.main">
        Could not load this version.
      </IgTypography>
    );
  }

  // The persisted source artifact is the domain's own encoding — a JSON
  // workspace manifest for multi-file domains, raw code for single-file ones.
  // Decode it through the domain codec so the view shows actual source rather
  // than dumping the manifest.
  const sourceFiles = artifacts.source
    ? Object.entries(decodeFiles(artifacts.source)).map(([name, content]) => ({
        name,
        content,
      }))
    : null;

  return (
    <Stack spacing={2}>
      {canImport ? (
        <Stack direction="row" justifyContent="flex-end">
          <IgButton
            size="small"
            variant="contained"
            disabled={readOnly}
            loading={isImporting}
            onClick={() => onImport(version)}
          >
            Edit as draft
          </IgButton>
        </Stack>
      ) : null}

      {extraRows}

      <Stack spacing={0.5}>
        <IgTypography variant="body2" fontWeight={600}>
          Dependencies
        </IgTypography>
        {detail.dependencies.length === 0 ? (
          <IgTypography variant="body2" color="text.secondary">
            None
          </IgTypography>
        ) : (
          <Stack spacing={0.5}>
            {detail.dependencies.map((dependency) => (
              <IgMonoText
                key={dependency.name}
                value={`${dependency.name}@${dependency.version}`}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Divider />

      <Stack spacing={0.5}>
        <IgTypography variant="body2" fontWeight={600}>
          Source
        </IgTypography>
        {sourceFiles ? (
          sourceFiles.map((file) => (
            <Stack key={file.name} spacing={0.5}>
              {sourceFiles.length > 1 ? <IgMonoText value={file.name} /> : null}
              <IgCodeEditor
                value={file.content}
                language={"ts"}
                readOnly={true}
                height={"50vh"}
              />
            </Stack>
          ))
        ) : (
          <IgTypography>Unavailable</IgTypography>
        )}
      </Stack>

      <Stack spacing={0.5}>
        <IgTypography variant="body2" fontWeight={600}>
          Generated tests
        </IgTypography>
        {artifacts.generatedTests ? (
          <IgCodeEditor
            value={artifacts.generatedTests}
            language={"ts"}
            readOnly={true}
            height={"50vh"}
          />
        ) : (
          <IgTypography>Unavailable</IgTypography>
        )}
      </Stack>

      <Stack spacing={0.5}>
        <IgTypography variant="body2" fontWeight={600}>
          User tests
        </IgTypography>
        {artifacts.userTests ? (
          <IgCodeEditor
            value={artifacts.userTests}
            language={"ts"}
            height={"50vh"}
            readOnly={true}
          />
        ) : (
          <IgTypography>Unavailable</IgTypography>
        )}
      </Stack>
    </Stack>
  );
};
